import {useQuery} from "@tanstack/react-query";
import {getLatestEventsForEventContainer} from "~/components/entityProfile/getLatestEventsForEventContainer";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {getEventContainersWithEvents} from "~/utilities/apis/eventContainersApi";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export function useCustomerProfileEventsJourney(args: {
    eventContainerId: Uuid | null | undefined;
    eventContainerTemplateId: Uuid | null | undefined;
    journeyWithEventsOverride?: EventContainerWithEvents;
    enabled?: boolean;
}): {
    journey: EventContainerWithEvents | null;
    isLoading: boolean;
    isError: boolean;
} {
    const {workspace} = useWorkspace();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    const eventContainerId = args.eventContainerId ?? null;
    const eventContainerTemplateId = args.eventContainerTemplateId ?? null;
    const enabled = args.enabled !== false;

    const query = useQuery({
        queryKey: ["customer-profile-events-journey", workspace.id, eventContainerTemplateId, eventContainerId],
        enabled: enabled && eventContainerId !== null && eventContainerTemplateId !== null && args.journeyWithEventsOverride === undefined,
        queryFn: async (): Promise<EventContainerWithEvents | null> => {
            if (eventContainerId === null || eventContainerTemplateId === null) {
                return null;
            }

            const eventContainersResult = await getEventContainersWithEvents({
                accessToken: heimdallUser.accessToken,
                id: eventContainerId,
                templateId: eventContainerTemplateId,
                limit: 2,
                offset: 0,
                intellsysBaseUrl,
                workspaceId: workspace.id,
            });

            if (eventContainersResult.success !== true) {
                throw eventContainersResult.error;
            }

            const eventContainers = eventContainersResult.data;
            if (eventContainers.length === 0) {
                return null;
            }
            if (eventContainers.length > 1) {
                throw new Error("Constraint violated: Multiple Event Containers found for the same ID");
            }

            return getLatestEventsForEventContainer({eventContainer: eventContainers[0]!});
        },
    });

    if (
        args.journeyWithEventsOverride !== undefined
        && eventContainerId !== null
        && args.journeyWithEventsOverride.id === eventContainerId
    ) {
        return {
            journey: args.journeyWithEventsOverride,
            isLoading: false,
            isError: false,
        };
    }

    if (eventContainerId === null) {
        return {
            journey: null,
            isLoading: false,
            isError: false,
        };
    }

    return {
        journey: query.data ?? null,
        isLoading: query.isLoading,
        isError: query.isError,
    };
}
